import { LoadingOutlined } from '@ant-design/icons';
import { T } from '@tolgee/react';
import { Dropdown, Menu, Space, Spin } from 'antd';
import { useState } from 'react';
import { LuCamera, LuClipboardPaste, LuPaperclip, LuPresentation } from 'react-icons/lu';
import styled from 'styled-components';

import { ArrowLeftSvg, ExclamationMarkSvg } from '../../../../assets/icons';
import GoogleDriveSvg from '../../../../assets/icons/GoogleDriveSvg';
import theme from '../../../../assets/theme';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useMetrics } from '../../../../hooks/useMetrics';
import { MaterialAttachment } from '../../../../hooks/useUploadMaterialAttachment';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import { UseMyTextModal } from '../UseMyTextModal';

export function MaterialsMenu({
  tenantId,
  onClickUploadFile,
  onClickPasteText,
  onFileUpload,
  onUploadSlides,
  onUploadFromGoogleDrive,
  isGoogleDriveReady,
  isGoogleDriveError,
  eventMetadata,
  isOpen,
  onOpenChange,
  fileOptionsEnabled,
  label,
}: {
  tenantId: string;
  onClickUploadFile: () => void;
  /**
   * Given, this replaces the built-in paste-in-text modal. A caller that
   * already has a text box on screen — the generator's passage field — wants
   * the teacher's cursor in that box, not in a modal that uploads a .txt only
   * to read it straight back out.
   */
  onClickPasteText?: () => void;
  onFileUpload: (file: MaterialAttachment) => void;
  /**
   * Local presentation upload — still deck-shaped, still the deck slot. Left
   * out when the caller has no deck slot to route a .pptx to.
   */
  onUploadSlides?: () => void;
  /**
   * Opens the Drive picker, which accepts anything the agent can read. Left
   * out when the caller can't take a Drive file.
   */
  onUploadFromGoogleDrive?: () => void;
  isGoogleDriveReady?: boolean;
  isGoogleDriveError?: boolean;
  eventMetadata: Record<string, unknown>;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Overrides the `add-material-file` gate on the two upload-a-file rows, for
   * callers whose own flag already decided the file paths are on.
   */
  fileOptionsEnabled?: boolean;
  /** Overrides the trigger button's label. */
  label?: React.ReactNode;
}) {
  const metrics = useMetrics();
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const isDesktop = useBreakPoint(700);
  const isAddMaterialFileFlagEnabled = useFeatureFlag('add-material-file');
  const isAddMaterialFileEnabled = fileOptionsEnabled ?? isAddMaterialFileFlagEnabled;

  // Use controlled state if props are provided, otherwise use internal state
  const dropdownOpen = isOpen !== undefined ? isOpen : internalOpen;
  const handleOpenChange = (open: boolean) => {
    if (onOpenChange) {
      onOpenChange(open);
    } else {
      setInternalOpen(open);
    }
  };

  return (
    <>
      <Dropdown
        open={dropdownOpen}
        onVisibleChange={handleOpenChange}
        // Mobile's trigger button sits in the composer at the bottom of the
        // screen, so the default bottomLeft placement pushed the menu off
        // the bottom of the viewport instead of onto it. Desktop has room
        // below, so it keeps the default.
        placement={isDesktop ? 'bottomLeft' : 'topLeft'}
        overlay={
          <Menu>
            <StyledMenuItem
              key="text-paste"
              onClick={() => {
                metrics.logEvent(
                  'CreatePage.UseMyMaterials.SelectPasteInTextOption',
                  eventMetadata,
                );
                if (onClickPasteText) {
                  onClickPasteText();
                } else {
                  setShowPasteModal(true);
                }
                handleOpenChange(false);
              }}
            >
              <IconWrapper height="16px" width="16px">
                {/* @ts-expect-error react-icons type mismatch */}
                <LuClipboardPaste />
              </IconWrapper>
              <span>
                <T
                  keyName="UseMyMaterials.Text.PasteOption"
                  defaultValue="Paste in text"
                />
              </span>
            </StyledMenuItem>

            <StyledMenuItem
              key="text-file-upload"
              disabled={!isAddMaterialFileEnabled}
              onClick={() => {
                metrics.logEvent(
                  'CreatePage.UseMyMaterials.SelectUploadFileOption',
                  eventMetadata,
                );
                onClickUploadFile();
                handleOpenChange(false);
              }}
            >
              <IconWrapper height="16px" width="16px">
                {/* @ts-expect-error react-icons type mismatch */}
                <LuPaperclip />
              </IconWrapper>
              <span>
                <T
                  keyName="UseMyMaterials.UploadFileOption"
                  defaultValue="Upload a file"
                />
              </span>
            </StyledMenuItem>
            <StyledMenuItem
              key="text-file-pic-upload"
              disabled={!isAddMaterialFileEnabled}
              onClick={() => {
                metrics.logEvent(
                  'CreatePage.UseMyMaterials.SelectUploadFileImageOption',
                  eventMetadata,
                );
                onClickUploadFile();
                handleOpenChange(false);
              }}
            >
              <IconWrapper height="16px" width="16px">
                {/* @ts-expect-error react-icons type mismatch */}
                <LuCamera />
              </IconWrapper>
              <span>
                <T
                  keyName="UseMyMaterials.UploadFileImageOption"
                  defaultValue="Upload an image or screenshot"
                />
              </span>
            </StyledMenuItem>
            {onUploadSlides ? (
              <StyledMenuItem
                key="slides-upload"
                onClick={() => {
                  metrics.logEvent(
                    'CreatePage.UseMyMaterials.SelectUploadSlidesOption',
                    eventMetadata,
                  );
                  onUploadSlides();
                  handleOpenChange(false);
                }}
              >
                <IconWrapper height="16px" width="16px">
                  {/* @ts-expect-error react-icons type mismatch */}
                  <LuPresentation />
                </IconWrapper>
                <span>
                  <T
                    keyName="UseMyMaterials.UploadPresentationOption"
                    defaultValue="Upload presentation (.pptx, .ppt, .key, .pdf)"
                  />
                </span>
              </StyledMenuItem>
            ) : null}
            {onUploadFromGoogleDrive ? (
              <StyledMenuItem
                key="google-drive"
                disabled={!isGoogleDriveReady}
                onClick={() => {
                  metrics.logEvent(
                    'CreatePage.UseMyMaterials.SelectGoogleDriveOption',
                    eventMetadata,
                  );
                  onUploadFromGoogleDrive();
                  handleOpenChange(false);
                }}
              >
                <IconWrapper height="16px" width="16px">
                  {isGoogleDriveError ? (
                    <ExclamationMarkSvg />
                  ) : !isGoogleDriveReady ? (
                    <Spin
                      indicator={
                        <LoadingOutlined
                          style={{ fontSize: 16, color: theme.colors.orange }}
                          spin
                        />
                      }
                    />
                  ) : (
                    <GoogleDriveSvg />
                  )}
                </IconWrapper>
                <span>
                  <T
                    keyName="UseMyMaterials.GoogleDriveOption"
                    defaultValue="Google Drive"
                  />
                </span>
              </StyledMenuItem>
            ) : null}
          </Menu>
        }
        trigger={['click']}
      >
        <AddResourcesDropdownButton type="white">
          <Space>
            <IconWrapper height="15px" width="15px">
              {/* @ts-expect-error react-icons type mismatch */}
              <LuPaperclip style={{ strokeWidth: '1.75px' }} />
            </IconWrapper>
            {isDesktop
              ? label ?? (
                  <T
                    keyName="ImportMaterials.button.label"
                    defaultValue="Add Materials"
                  />
                )
              : null}
          </Space>
          <IconWrapper style={{ transform: 'rotate(-90deg)' }} height="16px" width="16px">
            <ArrowLeftSvg />
          </IconWrapper>
        </AddResourcesDropdownButton>
      </Dropdown>

      <UseMyTextModal
        tenantId={tenantId}
        isVisible={showPasteModal}
        onCancel={() => setShowPasteModal(false)}
        onFileUploadSuccess={onFileUpload}
      />
    </>
  );
}

const StyledMenuItem = styled(Menu.Item)`
  & > span {
    display: grid !important;
    grid-template-columns: 24px 1fr;
    gap: 12px;
    align-items: center;

    .ant-dropdown-menu-item-icon {
      margin-right: 0 !important;
    }
  }
`;

const DropdownButton = styled(Button)`
  border: 1px solid ${theme.colors.greyLight};
  box-shadow: none;
  padding: 10px 15px;
  border-radius: 20px;
  background: none;
  height: 30px;
  display: flex;
  align-items: space-between;
  justify-content: center;
  gap: 5px;
  transition: all 0.2s ease;
  font-weight: 400;

  span {
    font-weight: 400;
  }
  font-size: 16px;
  color: ${theme.colors.black};
  svg {
    margin: 0px;
    height: 18px;
    width: 18px;
  }
`;

const AddResourcesDropdownButton = styled(DropdownButton)`
  border: 1px solid transparent;
  padding: 10px;
  height: 35px;
  border-radius: 10px;

  &:hover,
  &:focus {
    background-color: ${theme.colors.greyLight};
    border: 1px solid ${theme.colors.grey};
  }

  @media (max-width: 768px) {
    height: 32px;
    padding: 8px;
    font-size: 14px;
    color: ${theme.colors.greyDark};

    &:hover,
    &:focus {
      background-color: ${theme.colors.grey};
    }

    span {
      font-size: 14px;
      color: ${theme.colors.greyDark};
    }

    svg {
      color: ${theme.colors.greyDark};
    }
  }
`;
